import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { Popover } from 'antd'

import { getUserWorkingHoursDependingOnType } from '@vacationtracker/shared/functions/work-week'
import { getFormattedWorkhours } from '@vacationtracker/shared/functions/get-days-or-hours'

import { Stack } from '../../../../../../design-system/Stack'
import { Text } from '../../../../../../design-system/Text'
import { compactWorkWeek } from '../../../../../../components/work-week/compact'
import { useDayNames } from '../../../../../../hooks/use-day-names'
import IntlMessages from '../../../../../../util/IntlMessages'
import { ILocationUser } from '../../../../../../types/locations'

const LIST_MAX_HEIGHT = 320

export interface IOwnWorkWeekPopoverProps {
  /** Already filtered to the people who set their own week. */
  users: ILocationUser[]
  firstDayOfWeek: number
  /** The link that opens it. */
  children: React.ReactNode
}

/** Who on this location keeps their own work week, and what it is. Read-only. */
export const OwnWorkWeekPopover = (props: IOwnWorkWeekPopoverProps): React.ReactElement => {
  const { users, firstDayOfWeek, children } = props
  const { formatMessage } = useIntl()

  const dayNames = useDayNames()
  const shortDayNames = useMemo(() => dayNames.map((day) => day.short), [dayNames])

  const rows = useMemo(
    () =>
      users.map((user) => {
        // getFormattedWorkhours rotates Sunday to the end of the array in place, so it gets a copy -
        // the source here is react-query's cache. Shallow is enough: only the outer array moves.
        const normalized = getUserWorkingHoursDependingOnType(user.workHours)
        const hours = Array.isArray(normalized) ? [...normalized] : normalized
        return {
          id: user.id,
          name: user.name,
          schedule: `${compactWorkWeek({ days: user.workWeek ?? [], firstDayOfWeek, shortDayNames })} · ${getFormattedWorkhours(formatMessage, hours, firstDayOfWeek)}`,
        }
      }),
    [users, firstDayOfWeek, shortDayNames, formatMessage]
  )

  const content = (
    <div style={{ maxWidth: 380 }}>
      <Text tone="secondary" size="sm" block>
        <IntlMessages id="locationGeneral.ownWorkWeekExplain" />
      </Text>
      <Stack gap="xs" style={{ marginTop: 12, maxHeight: LIST_MAX_HEIGHT, overflowY: 'auto' }}>
        {rows.map((row) => (
          <Stack key={row.id} direction="horizontal" justify="space-between" align="center" gap="lg">
            <Text>{row.name}</Text>
            <Text tone="tertiary" size="sm">
              {row.schedule}
            </Text>
          </Stack>
        ))}
      </Stack>
    </div>
  )

  return (
    <Popover trigger="click" placement="bottomLeft" content={content}>
      {children}
    </Popover>
  )
}
